import React, { useMemo } from 'react';

import { expValEquals, expVal } from '@confluence/feature-experiments';
import { SuspenseWithDataFetcher } from '@confluence/query-preloader-tools/entry-points/SuspenseWithDataFetcher.ts';
import { HydrationSafeComponent } from '@confluence/hydration-utils/entry-points/HydrationSafeComponent.tsx';

import { getSmartLinksBoundaryNameForUrl } from './smartLinkBatchHelpers.ts';

// Suspense wrapper to be passed to ADF Renderer
function SuspenseWrapperForUrl({ url, children }) {
	if (!url || !expVal('confluence_smartlinks_suspense', 'isEnabled', false)) {
		return children;
	}
	// uses __SSR_SMARTLINKS_BOUNDARY_MAP__ for lookup
	const fetcherName = getSmartLinksBoundaryNameForUrl(url);

	// if no fetcherName, assume no fetcher was created and don't wrap children
	if (!fetcherName) {
		return children;
	}

	return (
		<SuspenseWithDataFetcher dataFetcherName={fetcherName}>
			<HydrationSafeComponent isEnabled>{children}</HydrationSafeComponent>
		</SuspenseWithDataFetcher>
	);
}

// Put initialState into a getter
export const useStoreOptions = (initialState: any, isPageSSRd: boolean): any =>
	useMemo(() => {
		const result: any = {};

		// Only include SuspenseWrapperForUrl if page was SSR'd
		if (isPageSSRd) {
			result.SuspenseWrapperForUrl = SuspenseWrapperForUrl;
		}

		// Include storeOptions if initialState exists and either:
		// - page was SSR'd, OR
		// - local cache is enabled
		if (
			initialState &&
			(isPageSSRd || expValEquals('platform_editor_smartlink_local_cache', 'isEnabled', true))
		) {
			result.storeOptions = {
				initialState,
			};
		}

		return result;
	}, [initialState, isPageSSRd]);
